import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { GoogleButton } from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success("Account created!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-2xl text-white">
            Joy<span className="text-fuchsia-400">Buzz</span>
          </span>
        </Link>

        <div className="bg-[#12141D] border border-white/10 rounded-3xl p-8 shadow-2xl animate-float-in">
          <h1 className="font-display text-2xl font-bold text-white text-center">Create account</h1>
          <p className="text-sm text-slate-400 text-center mt-1">Join and start chatting instantly</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="register-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Display name"
                className="w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 6 chars)"
                className="w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 outline-none transition-all"
              />
            </div>
            <button
              data-testid="register-submit"
              disabled={busy}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold rounded-xl py-3 shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
            >
              {busy ? "Creating..." : "Create account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-slate-500">oder</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <GoogleButton />

          <p className="text-center text-sm text-slate-400 mt-6">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-login" className="text-fuchsia-400 hover:text-fuchsia-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
